import { FilterMatchMode } from "primereact/api"
import { IColumnModel } from "../../../../models"
import { getItemWebStorage, setItemWebStorage } from "../../../../features/webStorageSaver"
import axios from "axios"
import { authorizeToCustomRest } from "../../../../features/customAuth/customAuth"
import { formatDateForFilter } from "../../../../features/dataFormatFunctions/dataFormatter"
import _ from "lodash"
import { getCustomTableImageBody, applyClickForTableBody } from "../../../../widgets/table/tableFeatures/tableTemplates/tableCellTemplates"
import { DataTableSelectEvent } from "primereact/datatable"
import { customFetchFileCUBA, customFetchFileJMIX } from "../../../../api/custom/documentsApi"
import { redirectToDocumentPreview } from "../customTableDocuments/customTableDocumentsFunctions"
import { store } from "../../../../stateManagement/store"
import { showToastMessage } from "../../../../stateManagement/reducers"
import { fileSizeBodyTemplate } from "../../../../widgets/table/tableFeatures/tableTemplates/tableBodyTemplates/documents"

export async function fetchCustomTableEnumColumnValues(enumApi: any, restAuthToken: any, authRequestOptions: any) {
    const { url, requestMethod } = enumApi ?? {}
    try {
        const response = await axios(url, {
            method: requestMethod,
            headers: {
                "Authorization": `Bearer ${restAuthToken}`
            },
        });
        const enumData = response?.data
        return enumData
    } catch (error: any) {
        if (error?.response?.status === 401) {
            await authorizeToCustomRest(authRequestOptions)
            return { error: 401 }
        }
    }
}

export async function fetchCustomTableObjectTypeDataCount(dataCountApi: any, restAuthToken: any, filter: any, authRequestOptions: any) {
    const { url, requestMethod } = dataCountApi ?? {}
    let queryParams, body
    if (requestMethod === 'POST') {
        body = { filter }
    }
    if (requestMethod === 'GET') {
        queryParams = { filter: JSON.stringify(filter) }
    }
    try {
        const response = await axios(url, {
            method: requestMethod,
            headers: {
                "Authorization": `Bearer ${restAuthToken}`
            },
            params: queryParams,
            data: body
        });
        const dataCount = response?.data
        return dataCount
    } catch (error: any) {
        if (error?.response?.status === 401) {
            await authorizeToCustomRest(authRequestOptions)
            return { error: 401 }
        }
    }
}

export async function lazyFetchCustomTableData(tableDataApi: any, restAuthToken: any, limit: number, offset: number, sort: string, filter: any, authRequestOptions: any) {
    const { url, fetchPlan, requestMethod } = tableDataApi ?? {}
    let queryParams, body
    if (requestMethod === 'POST') {
        body = { limit, offset, sort, fetchPlan, filter }
    }
    if (requestMethod === 'GET') {
        queryParams = { limit, offset, sort, fetchPlan, filter: JSON.stringify(filter) }
    }
    try {
        const response = await axios(url, {
            method: requestMethod,
            headers: {
                "Authorization": `Bearer ${restAuthToken}`
            },
            params: queryParams,
            data: body
        });
        const tableData = response?.data
        return { tableData }
    } catch (error: any) {
        if (error?.response?.status === 401) {
            await authorizeToCustomRest(authRequestOptions)
            return { error: 401 }
        }
        else {
            return { error }
        }
    }
}

export async function fetchCustomTableData(tableDataApi: any, restAuthToken: any, authRequestOptions: any, entityId?: any) {
    let { url, view, fetchPlan, requestMethod } = tableDataApi ?? {}
    let queryParams, body
    const numberOfAuthTry = store.getState().customAuth.numberOfAuthTry
    if (url.includes(':entityId')) {
        url = url.replace(':entityId', entityId)
    }
    if (requestMethod === 'POST') {
        body = { view, fetchPlan }
    }
    if (requestMethod === 'GET') {
        queryParams = { view, fetchPlan }
    }
    try {
        const response = await axios(url, {
            method: requestMethod,
            headers: {
                "Authorization": `Bearer ${restAuthToken}`
            },
            params: queryParams,
            data: body
        });
        const tableData = response?.data
        return { tableData }
    } catch (error: any) {
        if (error?.response?.status === 401) {
            if (numberOfAuthTry < 1) {
                store.dispatch(showToastMessage({ messageText: "Возникла ошибка при подключении к серверу", messageType: "error" }))
                await authorizeToCustomRest(authRequestOptions)
            }
        } else {
            store.dispatch(showToastMessage({ messageText: "Возникла ошибка при загрузке данных таблицы", messageType: "error" }))
        }
    }
}

export function saveCustomTableStateWebStorage(event: any, key: string) {
    let customTablesStateWebStorage = getItemWebStorage("customTablesState")
    let customTableStatePart: any = {
        [key]: event
    }
    const newCustomTablesStateWebStorage = { ...customTablesStateWebStorage, ...customTableStatePart }
    setItemWebStorage('customTablesState', newCustomTablesStateWebStorage)
}

export function prepareQueryArgsForCustomTableFetch(tableState: any, columns: any) {
    const { page = 0, sortField, sortOrder, filters } = tableState ?? {}
    let sort, offset = page * 100
    let filter: any = { conditions: [] }
    filters && Object.entries(filters).map(([field, filterOptions]: any) => {
        const col = columns.filter((col: any) => col.field === field)[0]
        const { value, matchMode } = filterOptions
        if (value != null && value != '') {
            let newFilter
            let filterValue = value
            if (col.valueType === 'date') {
                filterValue = formatDateForFilter(value?.replace(/\\./g, '-'))
            }
            if (matchMode === 'startsWith') {
                newFilter = {
                    property: field,
                    operator: "startsWith",
                    value: filterValue
                }
            }
            if (matchMode === 'contains') {
                newFilter = {
                    property: field,
                    operator: "contains",
                    value: filterValue
                }
            }
            if (matchMode === 'equals' && col.valueType === 'enum' && col.enumObject) {
                newFilter = {
                    property: field,
                    operator: "=",
                    value: filterValue
                }
            }
            if (matchMode === 'equals' && col.valueType !== 'enum') {
                newFilter = {
                    property: field,
                    operator: "=",
                    value: filterValue
                }
            }
            if (matchMode === 'gt') {
                newFilter = {
                    property: field,
                    operator: ">",
                    value: filterValue
                }
            }
            if (matchMode === 'gte') {
                newFilter = {
                    property: field,
                    operator: ">=",
                    value: filterValue
                }
            }
            if (matchMode === 'lt') {
                newFilter = {
                    property: field,
                    operator: "<",
                    value: filterValue
                }
            }
            if (matchMode === 'lte') {
                newFilter = {
                    property: field,
                    operator: "<=",
                    value: filterValue
                }
            }
            if (matchMode === 'notEquals') {
                newFilter = {
                    property: field,
                    operator: "<>",
                    value: filterValue
                }
            }
            filter.conditions.push(newFilter)
        }
    })
    const hasSortOrder = sortOrder != 0 && sortOrder != null
    sort = `${hasSortOrder ? `${sortOrder == 1 ? '+' : '-'}` : ''}${sortField ? sortField : ''}`
    if (sort.length === 0) {
        sort = null
    }
    return { sort, filter, offset }
}


export async function defineCustomTableColumnFilterOptions(type: string, headerName: string, enumObjectSource: any, enumObjectFromConfig: any, api: any, restAuthToken: any, restAuthRequestOptions: any) {
    let filterType = type === 'string' ? FilterMatchMode.CONTAINS : FilterMatchMode.EQUALS
    let filterPlaceholder = `${headerName} =`
    let filterSelectValues: any = []
    let enumObject: any = {}
    if (type === 'enum') {
        filterPlaceholder = "Все"
        if (enumObjectSource) {
            const { apiPath, valuesPath, propertyForKey, propertyForValue } = enumObjectSource ?? {}
            const customTablesEnumsWebStorage = getItemWebStorage("customTablesEnums")
            let savedEnumObject = customTablesEnumsWebStorage?.[apiPath]
            if (savedEnumObject) {
                filterSelectValues = Object.keys(savedEnumObject)
                enumObject = { ...savedEnumObject }
            } else {
                const enumApi = _.get(api, apiPath)
                const enumDataResponse = await fetchCustomTableEnumColumnValues(enumApi, restAuthToken, restAuthRequestOptions)
                const enumData = valuesPath ? _.get(enumDataResponse, valuesPath) : enumDataResponse
                enumData && enumData.map((enumDataObject: any) => {
                    filterSelectValues.push(enumDataObject[propertyForKey])
                    Object.assign(enumObject, { [enumDataObject[propertyForKey]]: enumDataObject[propertyForValue] })
                })
                if (Object.keys(enumObject).length !== 0) {
                    let customTablesEnumsPart: any = {
                        [apiPath]: enumObject
                    }
                    const newCustomTablesEnumsWebStorage = { ...customTablesEnumsWebStorage, ...customTablesEnumsPart }
                    setItemWebStorage('customTablesEnums', newCustomTablesEnumsWebStorage)
                }
            }
        } else if (enumObjectFromConfig) {
            filterSelectValues = Object.keys(enumObjectFromConfig)
            enumObject = { ...enumObjectFromConfig }
        }
    }
    return { filterType, filterPlaceholder, filterSelectValues, enumObject }
}

export async function defineCustomTableColumns(columnsStructure: any, api: any, restAuthToken: any, restAuthRequestOptions: any, apiType?: any) {
    let columns: Array<IColumnModel> = []
    for (const columnOptions of columnsStructure) {
        let { headerName, field, valueType, canBeFiltered = true, canBeSorted = true, visible = true, align, enumObjectSource, body: bodyOptions, enumObject: enumObjectFromConfig, valueFormat }: any = columnOptions ?? {}
        const { filterType, filterPlaceholder, filterSelectValues, enumObject }: any = await defineCustomTableColumnFilterOptions(valueType, headerName, enumObjectSource, enumObjectFromConfig, api, restAuthToken, restAuthRequestOptions)
        let column: IColumnModel = {
            headerName,
            pdfHeader: headerName,
            field,
            valueType,
            canBeFiltered,
            filterPlaceholder,
            filterType,
            showFilterMenu: valueType !== 'string',
            canBeSorted,
            align,
            visible,
            valueFormat
        }
        if (valueType === 'enum') {
            column = { ...column, filterSelectValues, enumObject }
        }
        if (bodyOptions) {
            align = "center"
            let { body }: any = defineColumnBody(bodyOptions, api, restAuthToken, restAuthRequestOptions, apiType)
            column = { ...column, body, align }
        }
        columns.push(column)
    }
    return columns
}

function defineColumnBody(bodyOptions: any, api: any, restAuthToken: any, restAuthRequestOptions: any, apiType: any) {
    const { type, onClick: onClickOptions } = bodyOptions ?? {}
    let body: any = () => { }
    let onClickFunction
    let getTableBodyFunction: any
    if (type === 'image') {
        getTableBodyFunction = getCustomTableImageBody.bind(null, bodyOptions)
    }
    if (type === "fileSize") {
        getTableBodyFunction = fileSizeBodyTemplate
    }
    if (onClickOptions) {
        onClickFunction = defineColumnBodyClickFunction(bodyOptions, api, restAuthToken, restAuthRequestOptions, apiType)
        body = applyClickForTableBody.bind(null, onClickFunction, onClickOptions, getTableBodyFunction)
    } else {
        body = getTableBodyFunction
    }
    return { body }
}

function defineColumnBodyClickFunction(bodyOptions: any, api: any, restAuthToken: any, restAuthRequestOptions: any, apiType: any) {
    const { onClick: onClickOptions } = bodyOptions ?? {}
    const { type, url, downloadSource } = onClickOptions ?? {}
    let onClickFunction
    if (type === 'download') {
        const downloadApi = _.get(api, downloadSource?.apiPath)
        if (downloadApi) {
            if (apiType === "jmix") {
                onClickFunction = customFetchFileJMIX.bind(null, downloadApi, restAuthToken, restAuthRequestOptions)
            }
            if (apiType === "cuba") {
                onClickFunction = customFetchFileCUBA.bind(null, downloadApi, restAuthToken, restAuthRequestOptions, true)
            }
        }
    }
    if (type === 'redirect.documentPreview') {
        onClickFunction = redirectToDocumentPreview
    }
    return onClickFunction
}

export function defineOnRowSelectFunction(id: any, objectType: any, onRowSelectOptions: any, tableAdditionalItem: any, navigate: any) {
    let onRowSelect
    const { type } = onRowSelectOptions ?? {}
    if (type === 'nestedTable') {
        const { propertyForEntityId, nestedTable } = tableAdditionalItem ?? {}
        let { url: nestedTableUrl, objectType: nestedObjectType } = nestedTable ?? {}
        onRowSelect = (event: DataTableSelectEvent) => {
            const entityId = event?.data?.[propertyForEntityId]
            if (nestedTableUrl) {
                nestedTableUrl = nestedTableUrl.replace(':id', `${id}`)
                nestedTableUrl = nestedTableUrl.replace(':objectType', `${objectType}`)
                nestedTableUrl = nestedTableUrl.replace(':entityId', `${entityId}`)
                nestedTableUrl = nestedTableUrl.replace(':nestedObjectType', `${nestedObjectType}`)
                navigate(`/app/${nestedTableUrl}`)
            }
        }
    }
    return onRowSelect
}

export function defineRowIsSelectableAndRowClassName(propertyForEnabledCheck: any, propertyForEnabledCheckType: any) {
    let rowIsSelectableCondition
    if (propertyForEnabledCheckType) {
        rowIsSelectableCondition = (data: any) => !!data?.[propertyForEnabledCheck]?.length
    } else {
        rowIsSelectableCondition = (data: any) => !!data?.[propertyForEnabledCheck]
    }
    const rowIsSelectable = (event: any) => rowIsSelectableCondition(event?.data)
    const rowClassName = (data: any) => (rowIsSelectableCondition(data) ? '' : 'p-disabled')
    return { rowIsSelectable, rowClassName }
}